import { createContext, useContext, useState } from "react"
import { ClientTheme, landtrackTheme, allThemes, darkThemeFor } from "@/app/lib/themes"
import { api } from "@/app/api/client"

type ThemeMode = "light" | "dark"

const MODE_STORAGE_KEY = "landtrack.themeMode"

interface ThemeContextValue {
  theme: ClientTheme
  /** Current light/dark mode. Only affects themes with a dark counterpart. */
  mode: ThemeMode
  toggleMode: () => void
  /** Whether the active client theme has a dark counterpart (drives toggle visibility). */
  hasDarkVariant: boolean
  setThemeById: (id: string) => void
  allThemes: ClientTheme[]
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: landtrackTheme,
  mode: "light",
  toggleMode: () => {},
  hasDarkVariant: false,
  setThemeById: () => {},
  allThemes: [],
})

export const useClientTheme = () => useContext(ThemeContext)

function resolveInitialTheme(): ClientTheme {
  const id =
    document.querySelector<HTMLMetaElement>('meta[name="client-theme"]')?.content ?? "landtrack"
  return allThemes.find((t) => t.clientId === id) ?? landtrackTheme
}

function resolveInitialMode(): ThemeMode {
  try {
    return localStorage.getItem(MODE_STORAGE_KEY) === "dark" ? "dark" : "light"
  } catch {
    return "light"
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [baseTheme, setBaseTheme] = useState<ClientTheme>(resolveInitialTheme)
  const [mode, setMode] = useState<ThemeMode>(resolveInitialMode)

  const setThemeById = (id: string) => {
    const found = allThemes.find((t) => t.clientId === id)
    if (!found) return
    setBaseTheme(found)
    // Persist to backend (admin-only gate is enforced server-side)
    api.patch("/settings", { theme: id }).catch(() => {})
  }

  const toggleMode = () => {
    // Swap every themed colour in one frame: per-element transitions (rows,
    // pills, toggles) run at different speeds and produce a mismatched
    // crossfade mid-flip, so suppress them for the swap.
    document.documentElement.classList.add("theme-switching")
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove("theme-switching")
      })
    })
    setMode((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark"
      try {
        localStorage.setItem(MODE_STORAGE_KEY, next)
      } catch {
        // Storage unavailable (private browsing) — mode still applies for the session.
      }
      return next
    })
  }

  const darkVariant = darkThemeFor[baseTheme.clientId]
  const theme = mode === "dark" && darkVariant ? darkVariant : baseTheme

  return (
    <ThemeContext.Provider
      value={{
        theme,
        mode,
        toggleMode,
        hasDarkVariant: !!darkVariant,
        setThemeById,
        allThemes,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
